import { Injectable, inject } from '@angular/core';
import { Browser, OpenOptions } from '@capacitor/browser';
import { LoggingService } from './logging.service';
import { NativeDocumentService } from './native-document.service';
import { PlatformService } from './platform.service';

@Injectable({ providedIn: 'root' })
export class BrowserService {
  private readonly platform = inject(PlatformService);
  private readonly nativeDocumentService = inject(NativeDocumentService);
  private readonly logger = inject(LoggingService);

  private readonly TOOLBAR_COLOR = '#410161';

  async openUrl(
    url: string,
    options?: {
      presentationStyle?: 'fullscreen' | 'popover';
      toolbarColor?: string;
    }
  ): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const browserOptions: OpenOptions = {
      url,
      presentationStyle: options?.presentationStyle ?? 'fullscreen',
      toolbarColor: options?.toolbarColor ?? this.TOOLBAR_COLOR,
    };

    await Browser.open(browserOptions);
  }

  async close(): Promise<void> {
    if (this.platform.isNativeMobile()) {
      try {
        await Browser.close();
      } catch {
        // Browser may not be open, ignore errors
      }
    }
  }

  async addFinishedListener(callback: () => void): Promise<() => void> {
    if (!this.platform.isNativeMobile()) {
      return () => undefined;
    }

    const listener = await Browser.addListener('browserFinished', callback);
    return () => listener.remove();
  }

  async addPageLoadedListener(callback: () => void): Promise<() => void> {
    if (!this.platform.isNativeMobile()) {
      return () => undefined;
    }

    const listener = await Browser.addListener('browserPageLoaded', callback);
    return () => listener.remove();
  }

  async openLocalAsset(relativePath: string): Promise<void> {
    const url = relativePath.startsWith('/')
      ? window.location.origin + relativePath
      : relativePath;

    if (!this.platform.isNativeMobile()) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.logger.logWarning(`BrowserService: failed to fetch asset ${relativePath} (${response.status})`);
        return;
      }
      const blob = await response.blob();
      const filename = relativePath.split('/').pop() ?? 'document.pdf';
      await this.nativeDocumentService.openDocument(blob, filename);
    } catch (error) {
      this.logger.logException(error, `BrowserService: error opening local asset ${relativePath}`);
    }
  }
}
